import { useFormik } from 'formik';
import React, { FC } from 'react';
import PhoneInput, {
    formatPhoneNumberIntl,
    isValidPhoneNumber,
} from 'react-phone-number-input';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Col, Row } from 'react-grid-system';
import { useSettings } from '../../../contexts/settingsContext';
import { useReturnContext } from '../../../contexts/returnContext';
import { useTranslation } from '../../../contexts/translationContext';
import { EASYCOM_ROUTES } from '../../../navigation';
import '../../../utils/phoneInputStyle.css';
import { theme } from '../../../utils/theme';
import Spacing from '../../atoms/Spacing';
import Typography from '../../atoms/Typography';
import PrimaryButton from '../../molecules/PrimaryButton';
import TextInput from '../../molecules/TextInput';
import { useAppNavigate } from '../../../hooks/useAppNavigate';

const StyledErrorMsg = styled(Typography)`
  padding: 0 0 0 8px;
  margin-top: 8px;
  color: ${theme.colors.red[100]};
`;

interface IfieldTable {
  title: string;
  placeholder: string;
  name: string;
  translateKey: string;
  sizes: Record<string, number>;
}

interface Props {
  setPostReturnError: (error: boolean) => void;
}

const fieldTable: IfieldTable[] = [
  {
    title: 'label_first_name',
    placeholder: 'placeholder_first_name',
    name: 'firstName',
    translateKey: 'firstname_field',
    sizes: {
      xs: 12,
      md: 12,
      lg: 6,
    },
  },
  {
    title: 'label_last_name',
    placeholder: 'placeholder_last_name',
    name: 'lastName',
    translateKey: 'lastname_field',
    sizes: {
      xs: 12,
      md: 12,
      lg: 6,
    },
  },
  {
    title: 'your_email',
    placeholder: 'placeholder_email_field',
    name: 'email',
    translateKey: 'email_field',
    sizes: {
      xs: 12,
      md: 12,
      lg: 12,
    },
  },
];

const FreightBookingForm: FC<Props> = ({ setPostReturnError }) => {
  const navigate = useAppNavigate();
  const { translate } = useTranslation();
  const {
    setPickupContactInfo,
    loading: loadingSendingReturns,
    postReturn,
    setReturns,
    pickupContactInfo,
    returns,
  } = useReturnContext();
  const { shippingFormEnabled } = useSettings();

  const useShippingForm = shippingFormEnabled
            && Object.values(returns).some((x) => x.returnData.isClaim);

  const initialValues = {
    firstName: pickupContactInfo?.firstName || '',
    lastName: pickupContactInfo?.lastName || '',
    email: pickupContactInfo?.email || '',
    phoneNumber: pickupContactInfo?.phoneNumber || '',
  };

  const validationSchema = Yup.object({
    firstName: Yup.string().required(
      `${translate('placeholder_first_name', null)} ${translate(
        'is_required',
        null,
      )}`,
    ),
    lastName: Yup.string().required(
      `${translate('placeholder_last_name', null)} ${translate(
        'is_required',
        null,
      )}`,
    ),
    email: Yup.string()
      .required(`${translate('email', null)} ${translate('is_required', null)}`)
      .email(translate('must_be_valid_email', null)),
    phoneNumber: Yup.string()
      .required(`${translate('phone', null)} ${translate('is_required', null)}`)
      .test(
        'customPhoneNumberValidation',
        translate('invalid_phone_number', null),
        // eslint-disable-next-line func-names
        function (value) {
          const { path, createError } = this;
          if (!value || !isValidPhoneNumber(value)) {
            return createError({
              path,
              message: translate('invalid_phone_number', null),
            });
          }
          return true;
        },
      ),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const newContactInfo = {
        ...values,
        phoneNumber: formatPhoneNumberIntl(values.phoneNumber),
      };
      setPickupContactInfo({ ...newContactInfo });

      if (useShippingForm) {
          navigate(EASYCOM_ROUTES.SHIPPING);
      } else {
        const result = await postReturn(newContactInfo);

        if (result.status === 'Failure') {
          setPostReturnError(true);
        } else {
          setReturns({});
          navigate(EASYCOM_ROUTES.RETURNS_COMPLETE);
        }
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Row>
        <Col xs={12} md={12} lg={8}>
          <Typography
            align="left"
            translateKey="enter_shipping_details"
            variant="h4"
          />
          <Spacing spacing={3} />
          <Row gutterWidth={16}>
            {fieldTable?.map((field) => (
              <Col
                key={field.name}
                xs={field.sizes.xs}
                md={field.sizes.md}
                lg={field.sizes.lg}
              >
                <TextInput
                  title={field.title}
                  placeholder={field.placeholder}
                  name={field.name}
                  translateKey={field.translateKey}
                  value={formik.values[field.name]}
                  error={
                    formik.touched[field.name] && formik.errors[field.name]
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    formik.setFieldValue(field.name, e.target.value)
                  }
                />
                <Spacing spacing={3} />
              </Col>
            ))}
          </Row>
          <Typography translateKey="phone_field" variant="input-label" />
          <PhoneInput
            style={{ paddingLeft: 8 }}
            placeholder={translate('placeholder_phone_field', null)}
            defaultCountry="SE"
            value={formik.values.phoneNumber}
            name="phone"
            onChange={(phoneNumber) =>
              formik.setFieldValue('phoneNumber', phoneNumber)
            }
          />
          {formik.touched.phoneNumber && formik.errors.phoneNumber && (
            <StyledErrorMsg variant="input-label">
              {formik.errors.phoneNumber}
            </StyledErrorMsg>
          )}

          <Spacing spacing={3} />
          <PrimaryButton
            disabled={loadingSendingReturns}
            loading={loadingSendingReturns}
            translateKey={useShippingForm ? 'next_shipping' : 'send_returns'}
            icon={useShippingForm ? 'arrow-right' : 'send-package'}
            type="submit"
          />
          <Spacing spacing={4} />
        </Col>
      </Row>
    </form>
  );
};

export default FreightBookingForm;
